import { cubTheme, tokens } from '@cub-com-br/theme';
import { Theme, ThemeOptions, alpha, createTheme, darken, lighten } from '@mui/material/styles';
import { BrandingConfig } from '~/api/types';

/** Colors with no `@cub-com-br/theme` equivalent. Fixed for every tenant. */
export const colors = {
  lightPurple: '#878CDC',
};

type BrandingColors = Required<Omit<BrandingConfig, 'companyLogoUrl' | 'faviconUrl'>>;

export const defaultBranding: BrandingColors = {
  primaryColor: cubTheme.palette.primary.main,
  // cubTheme's palette.secondary.main is midnight; CUB's brand secondary is cobalt
  secondaryColor: tokens.cobalt,
  primaryContrastColor: cubTheme.palette.primary.contrastText,
  secondaryContrastColor: cubTheme.palette.secondary.contrastText,
  backgroundColor: tokens.grey200,
};

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * The components that carry the tenant's brand. Everything else keeps the design
 * system's own colors, so a tenant palette can't be mistaken for a state signal
 * (e.g. a red input reading as a validation error).
 */
const brandedComponents = ['MuiButton', 'MuiButtonGroup', 'MuiChip', 'MuiFab', 'MuiLink', 'MuiBottomNavigation'];

/**
 * Swaps the design system's colors for the tenant's. Setting the palette is not
 * enough — cubTheme writes its colors directly into the component styles. The few
 * that aren't written as a plain hex are handled below.
 */
function retintBrandedComponents(
  components: ThemeOptions['components'],
  colorMap: Record<string, string>,
): ThemeOptions['components'] {
  const patterns = Object.entries(colorMap).map(([hex, to]) => ({ regex: new RegExp(escapeRegExp(hex), 'gi'), to }));
  const replace = (value: unknown): unknown => {
    if (typeof value === 'string') return patterns.reduce((acc, { regex, to }) => acc.replace(regex, to), value);
    if (Array.isArray(value)) return value.map(replace);
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value).map(([key, nested]) => [key, replace(nested)]));
    }
    return value; // function-form styleOverrides are left alone
  };
  return Object.fromEntries(
    Object.entries(components ?? {}).map(([name, overrides]) => [
      name,
      brandedComponents.includes(name) ? replace(overrides) : overrides,
    ]),
  ) as ThemeOptions['components'];
}

function definedBranding(branding?: BrandingConfig | null): Partial<BrandingConfig> {
  const entries = Object.entries(branding ?? {}).filter(
    ([, value]) => typeof value === 'string' && value.trim() !== '',
  );
  return Object.fromEntries(entries);
}

export function createAppTheme(branding?: BrandingConfig | null): Theme {
  const merged: BrandingColors = { ...defaultBranding, ...definedBranding(branding) };

  const primaryLight = lighten(merged.primaryColor, 0.2);
  const primaryDark = darken(merged.primaryColor, 0.3);
  const secondaryLight = lighten(merged.secondaryColor, 0.2);
  const secondaryDark = darken(merged.secondaryColor, 0.3);

  const base = createTheme(cubTheme, {
    palette: {
      // light/dark are set explicitly: createTheme only derives them when absent,
      // and merging onto cubTheme carries its own blue shades through otherwise.
      primary: {
        main: merged.primaryColor,
        light: primaryLight,
        dark: primaryDark,
        contrastText: merged.primaryContrastColor,
      },
      secondary: {
        main: merged.secondaryColor,
        light: secondaryLight,
        dark: secondaryDark,
        contrastText: merged.secondaryContrastColor,
      },
      // Overridable per tenant; defaults to the design system's neutral so brand
      // colors read consistently against it.
      background: { default: merged.backgroundColor },
    },
    components: retintBrandedComponents(cubTheme.components, {
      [cubTheme.palette.primary.main]: merged.primaryColor,
      [cubTheme.palette.secondary.main]: merged.secondaryColor,
    }),
  });

  // Hover tints derived from other brand tokens that don't textually match
  // primaryColor, so they are hand-tuned here.
  return createTheme(base, {
    components: {
      MuiButton: {
        styleOverrides: {
          // Every button is medium — none of them passes `size`. Medium on desktop,
          // large on mobile: MUI's `size` prop takes no responsive value, so the
          // medium metrics grow into the large ones at the breakpoint.
          sizeMedium: {
            [base.breakpoints.down('md')]: cubTheme.components?.MuiButton?.styleOverrides?.sizeLarge,
          },
          containedPrimary: {
            // primaryContrastColor is hand-tuned: cubTheme's
            // containedPrimary.color is tokens.white, and blanket-replacing
            // "white" would hit unrelated buttons too (error, etc.).
            'color': merged.primaryContrastColor,
            '&:hover': { backgroundColor: darken(merged.primaryColor, 0.18) },
          },
          outlinedPrimary: {
            '&:hover': { backgroundColor: alpha(merged.primaryColor, 0.09) },
          },
          textPrimary: {
            '&:hover': {
              backgroundColor: alpha(merged.primaryColor, 0.17),
              color: darken(merged.primaryColor, 0.2),
            },
          },
        },
      },
      // Checkboxes and radios take their tick from palette.primary at runtime; pinned
      // so a form never mixes two accent colors.
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: alpha(tokens.blue, 0.04) },
            '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: tokens.blue },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: alpha(tokens.blue, 0.04) },
            '&.Mui-checked': { color: tokens.blue },
          },
        },
      },
    },
  });
}
